import {
  Box,
  Grid,
  IconButton,
  Pagination,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { GetApp } from '@mui/icons-material';
import DataCard from './DataCard';

const DataList = (props: any) => {
  const isSmallScreen = useMediaQuery('(min-width:800px)');

  const {
    columns,
    rows,
    getRowId = (row: any) => row.id,
    initialState = {},
    processRowUpdate = (row: any) => row,
    exportFields,
    minimal = false,
    excelExportOptions = { fileName: 'export', disabled: false },
    ...rest
  } = props;
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    return rows.filter((row: any) =>
      columns.some((column: any) =>
        row[column.field]
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    );
  }, [rows, columns, searchQuery]);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const cardsPerPage = 4;
  const totalCards = filteredRows.length;
  const totalPages = Math.ceil(totalCards / cardsPerPage);
  const startIndex = (currentPage - 1) * cardsPerPage;
  const endIndex = startIndex + cardsPerPage;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  function convertObjectsToArrays(objects: any[]) {
    if (!Array.isArray(objects)) {
      throw new Error('Input must be an array of objects');
    }

    if (objects.length === 0) {
      return [];
    }

    const keys = Object.keys(objects[0]).filter(
      (x) => typeof objects[0][x] !== 'object'
    );

    const headers = keys.map((key) => key.toUpperCase());
    const dataRows = objects.map((object) => {
      if (typeof object !== 'object') {
        throw new Error('Elements of the input array must be objects');
      }

      return keys.map((key) => {
        const value = object[key];
        if (
          typeof value === 'string' &&
          value.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}Z$/)
        ) {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return value;
          }
          return date.toLocaleString();
        }

        return value;
      });
    });

    return [headers, ...dataRows];
  }

  async function exportToExcel(data: any) {
    const array = convertObjectsToArrays(data);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Adatsor');
    array.forEach((row) => worksheet.addRow(row));
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      excelExportOptions.fileName + '_' + new Date().toLocaleString() + '.xlsx';
    link.click();
    URL.revokeObjectURL(url);
  }

  const csvOptions = {
    header: true,
    delimiter: ';',
    fileName: `export_${new Date().toLocaleString()}`,
    columnDelimiter: ';',
    columnSeparator: ';',
    fileExtension: 'csv',
    decimalSeparator: '.',
    showTitle: true,
    title: `${new Date().toLocaleString()}`,
    utf8WithBom: true,
  };

  const printOptions = {
    title: `export_${new Date().toLocaleString()}`,
    fileName: `export_${new Date().toLocaleString()}`,
    landscape: true,
    width: '100%',
    height: '100%',
    printBackground: false,
    scale: 1,
    hideToolbar: true,
    useCss: true,
    hideFooter: true,
    fields: exportFields,
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <Grid container sx={{
        justifyContent: "space-between"
      }}>
        <Grid>
          <GridToolbarQuickFilter />
        </Grid>
        {!minimal && (
          <Grid>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
            <GridToolbarExport
              csvOptions={csvOptions}
              printOptions={printOptions}
            />
            {!excelExportOptions.disabled && (
              <Tooltip title="Exportálás excelbe">
                <IconButton color="primary" onClick={() => exportToExcel(rows)}>
                  <GetApp />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        )}
      </Grid>
    </GridToolbarContainer>
  );

  return (
    <Box
      sx={{
        height: isSmallScreen ? '50svh' : '',
        width: '100%',
        // Allow DataGrid action menus to escape the clipped grid viewport.
        '& .MuiDataGrid-root': { overflow: 'visible' },
        '& .MuiDataGrid-main': { overflow: 'visible' },
        '& .MuiDataGrid-virtualScroller': { overflow: 'auto !important' },
      }}
    >
      {isSmallScreen ? (
        <DataGrid
          density={'compact'}
          rows={rows}
          initialState={initialState}
          columns={columns}
          editMode="row"
          pagination
          pageSizeOptions={[5, 10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          slots={{ toolbar: CustomToolbar }}
          showToolbar
          processRowUpdate={processRowUpdate}
          getRowId={getRowId}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          {...rest}
        />
      ) : (
        <Grid>
          <Grid container spacing={1} sx={{
            paddingBottom: 2
          }}>
            <Grid size={12}>
              <TextField
                fullWidth
                placeholder="Keresés..."
                value={searchQuery}
                onChange={(e: any) => setSearchQuery(e.target.value)}
              />
            </Grid>
            <IconButton
              color="primary"
              onClick={() => exportToExcel(filteredRows)}
            >
              <GetApp />
            </IconButton>
            {filteredRows.slice(startIndex, endIndex).map((row: any) => (
              <Grid key={getRowId(row)} size={12}>
                <DataCard row={row} headers={columns} {...props} />
              </Grid>
            ))}
          </Grid>
          <Grid container sx={{
            justifyContent: "center"
          }}>
            <Grid>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_event, value) => handlePageChange(value)}
                color="primary"
                size="small"
                siblingCount={0}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DataList;
